import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authAPI } from '../services/api';
import { useAuth } from './AuthContext';

const GlobalViewContext = createContext(null);
const STORAGE_KEY = 'famapos_global_view_expires_at';

export const GlobalViewProvider = ({ children }) => {
  const { user } = useAuth();
  const [expiresAt, setExpiresAt] = useState(() => Number(sessionStorage.getItem(STORAGE_KEY) || 0));
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem(STORAGE_KEY);
      setExpiresAt(0);
    }
  }, [user]);

  const isGlobalView = Boolean(user && ['manager', 'admin'].includes(user.role) && expiresAt > now);

  useEffect(() => {
    if (expiresAt && expiresAt <= now) {
      sessionStorage.removeItem(STORAGE_KEY);
      setExpiresAt(0);
    }
  }, [expiresAt, now]);

  const unlockGlobalView = useCallback(async (pin) => {
    const response = await authAPI.unlockGlobalView(pin);
    const nextExpiry = Date.now() + Number(response.data?.expires_in_seconds || 1800) * 1000;
    sessionStorage.setItem(STORAGE_KEY, String(nextExpiry));
    setExpiresAt(nextExpiry);
    setNow(Date.now());
    return response.data;
  }, []);

  const lockGlobalView = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setExpiresAt(0);
  }, []);

  const value = useMemo(() => ({
    isGlobalView,
    canUseGlobalView: Boolean(user && ['manager', 'admin'].includes(user.role)),
    unlockGlobalView,
    lockGlobalView,
    expiresAt,
  }), [isGlobalView, user, unlockGlobalView, lockGlobalView, expiresAt]);

  return <GlobalViewContext.Provider value={value}>{children}</GlobalViewContext.Provider>;
};

export const useGlobalView = () => {
  const value = useContext(GlobalViewContext);
  if (!value) throw new Error('useGlobalView must be used within GlobalViewProvider');
  return value;
};
